import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Filter, Search, Layers3 } from 'lucide-react'
import { AgGridReact, type CustomCellRendererProps } from 'ag-grid-react'
import {
  AllCommunityModule,
  ModuleRegistry,
  type ColDef,
} from 'ag-grid-community'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { useWaveRollupSelectionStore } from '@/stores/useWaveRollupSelectionStore'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Pagination } from '@/components/common/Pagination'
import { usePersistedPageSize } from '@/hooks/usePersistedPageSize'
import { TABLE_PAGE_SIZE_KEYS } from '@/stores/useTablePageSizeStore'
import { WAVE_CALENDARS, WAVE_FIXTURES } from '@/components/WavePlanning/data'
import {
  WAVE_PLANS,
  wavePlanMfpLabel,
  wavePlanPath,
  waveRollupPath,
  type WavePlan,
} from '@/components/WavePlanning/plans'
import { WavePlanStatus } from '@/components/WavePlanning/WavePlanStatus'

ModuleRegistry.registerModules([AllCommunityModule])

const statuses = [...new Set(WAVE_PLANS.map(plan => plan.status))]
const subclassLabel = (plan: WavePlan) =>
  `${plan.subclassId} · ${WAVE_FIXTURES.find(fixture => fixture.id === plan.subclassId)!.name}`

const columnDefs: ColDef<WavePlan>[] = [
  {
    field: 'name',
    headerName: 'Plan Name',
    flex: 2,
    minWidth: 260,
    cellRenderer: ({ data }: CustomCellRendererProps<WavePlan>) =>
      data && (
        <div className="flex min-w-0 flex-col justify-center gap-1 leading-normal">
          <Link
            to={wavePlanPath(data.id)}
            onClick={event => event.stopPropagation()}
            className="truncate text-sm font-medium text-primary hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
          >
            {data.name}
          </Link>
          <span className="truncate text-xs text-muted-foreground">
            {subclassLabel(data)}
          </span>
        </div>
      ),
  },
  {
    field: 'status',
    headerName: 'Status',
    minWidth: 150,
    flex: 1,
    cellRenderer: ({ data }: CustomCellRendererProps<WavePlan>) =>
      data && <WavePlanStatus status={data.status} />,
  },
  {
    colId: 'mfps',
    headerName: 'MFP Plan',
    minWidth: 240,
    flex: 2,
    valueGetter: ({ data }) => (data ? wavePlanMfpLabel(data) : ''),
    tooltipValueGetter: ({ value }) => value,
    wrapText: true,
    autoHeight: true,
    cellClass: 'text-sm',
  },
  {
    colId: 'calendar',
    headerName: 'Calendar',
    minWidth: 210,
    flex: 1,
    valueGetter: ({ data }) =>
      WAVE_CALENDARS.find(calendar => calendar.id === data?.calendarId)?.label,
    cellClass: 'text-sm',
  },
]

function PlanSelectionCell({ data }: CustomCellRendererProps<WavePlan>) {
  const { selectedPlanIds, selectPlans } = useWaveRollupSelectionStore()
  return (
    data && (
      <div
        data-rollup-selection
        className="flex h-full items-center justify-center"
        onClick={event => event.stopPropagation()}
      >
        <Checkbox
          aria-label={`Select ${data.name}`}
          checked={selectedPlanIds.includes(data.id)}
          onCheckedChange={checked => selectPlans([data.id], checked)}
          className="peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2"
        />
      </div>
    )
  )
}

function PlanSelectionHeader({ planIds }: { planIds: string[] }) {
  const { selectedPlanIds, selectPlans } = useWaveRollupSelectionStore()
  const count = planIds.filter(id => selectedPlanIds.includes(id)).length
  return (
    <Checkbox
      aria-label="Select all filtered plans"
      title="Select all filtered plans across all pages"
      checked={planIds.length > 0 && count === planIds.length}
      indeterminate={count > 0 && count < planIds.length}
      disabled={!planIds.length}
      onCheckedChange={checked => selectPlans(planIds, checked)}
      className="peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2"
    />
  )
}

export default function WavePlans() {
  const navigate = useNavigate()
  const { selectedPlanIds, setSelectedPlanIds } = useWaveRollupSelectionStore()
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('all')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = usePersistedPageSize(
    TABLE_PAGE_SIZE_KEYS.WAVE_PLANS_LIST,
    20
  )
  const plans = useMemo(() => {
    const query = search.trim().toLowerCase()
    return WAVE_PLANS.filter(
      plan =>
        (status === 'all' || plan.status === status) &&
        `${plan.name} ${subclassLabel(plan)}`.toLowerCase().includes(query)
    )
  }, [search, status])
  const selectableColumns = useMemo<ColDef<WavePlan>[]>(
    () => [
      {
        colId: 'rollupSelection',
        headerName: '',
        width: 52,
        pinned: 'left',
        sortable: false,
        resizable: false,
        headerComponent: PlanSelectionHeader,
        headerComponentParams: { planIds: plans.map(plan => plan.id) },
        cellRenderer: PlanSelectionCell,
      },
      ...columnDefs,
    ],
    [plans]
  )
  const totalPages = Math.max(1, Math.ceil(plans.length / pageSize))
  const currentPage = Math.min(page, totalPages)

  return (
    <div className="flex min-w-0 flex-col gap-6 p-4 md:p-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            Wave Planning
          </h1>
          <p className="mt-1 text-base text-foreground">
            Open a plan to manage allocations, or select plans to view a rollup.
          </p>
        </div>
        <p className="pt-1 text-xs text-muted-foreground">
          Demo data · Changes reset on reload
        </p>
      </header>
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card p-3">
        <div className="relative w-full sm:max-w-[352px] sm:flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            aria-label="Search plans"
            placeholder="Search plans..."
            value={search}
            onChange={event => {
              setSearch(event.target.value)
              setPage(1)
            }}
            className="bg-muted/50 pl-9"
          />
        </div>
        <div className="flex w-full items-center gap-3 sm:w-auto">
          <Filter className="h-5 w-5 shrink-0 text-muted-foreground" />
          <Select
            value={status}
            onValueChange={value => {
              setStatus(value)
              setPage(1)
            }}
          >
            <SelectTrigger
              aria-label="Filter by status"
              className="min-w-0 flex-1 sm:w-[160px]"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {statuses.map(value => (
                <SelectItem key={value} value={value}>
                  {value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <p className="text-xs text-muted-foreground sm:ml-auto" role="status">
          {plans.length} {plans.length === 1 ? 'plan' : 'plans'}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <p
          className="text-sm font-medium"
          role="status"
          data-testid="rollup-selection-count"
        >
          {selectedPlanIds.length} selected
        </p>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSelectedPlanIds(WAVE_PLANS.map(plan => plan.id))}
        >
          Select all plans
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={!selectedPlanIds.length}
          onClick={() => setSelectedPlanIds([])}
        >
          Clear selection
        </Button>
        <Button
          className="sm:ml-auto"
          disabled={!selectedPlanIds.length}
          onClick={() => navigate(waveRollupPath(selectedPlanIds))}
        >
          <Layers3 size={16} /> View Rollup
        </Button>
      </div>
      <div className="min-w-0 overflow-hidden border border-[#e7e7e7] bg-white">
        <div className="ag-theme-michaels w-full" data-testid="wave-plans-grid">
          <AgGridReact<WavePlan>
            rowData={plans.slice(
              (currentPage - 1) * pageSize,
              currentPage * pageSize
            )}
            columnDefs={selectableColumns}
            getRowId={({ data }) => data.id}
            theme="legacy"
            domLayout="autoHeight"
            rowHeight={68}
            headerHeight={48}
            overlayNoRowsTemplate="<span>No plans found</span>"
            suppressCellFocus
            suppressMovableColumns
            suppressColumnVirtualisation
            rowClass="cursor-pointer"
            onRowClicked={({ data, event }) => {
              if (
                event?.target instanceof Element &&
                event.target.closest('[data-rollup-selection]')
              )
                return
              if (data) navigate(wavePlanPath(data.id))
            }}
          />
        </div>
        <div className="[&>div]:flex-wrap [&>div]:gap-3">
          <Pagination
            page={currentPage}
            pageSize={pageSize}
            totalPages={totalPages}
            onPageChange={setPage}
            onPageSizeChange={value => {
              setPageSize(value)
              setPage(1)
            }}
          />
        </div>
      </div>
    </div>
  )
}
